import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { applicationApi } from '@/api/endpoints'
import type { ApplicationDetail } from '@/types/api'

/**
 * A run that stopped for the person's answers on a worker that can keep its
 * page (the Windows Chrome): the tab is still open there. This says so, shows
 * the page as it stands, and tells them that "Save & submit" continues the
 * run on that very screen — nothing is typed twice, no account is made again.
 * Nothing is shown when no page is parked (the API checks the worker live).
 */
export function ParkedBanner({ application }: { application: ApplicationDetail }) {
  const id = application.id
  const live = useQuery({
    queryKey: ['application-live', id, 'parked'],
    queryFn: () => applicationApi.live(id),
    enabled: application.status === 'needs_you',
    refetchInterval: 5000,
    retry: false,
  })
  const parked = Boolean(live.data?.waiting) && live.data?.reason === 'parked'
  const shot = useQuery({
    queryKey: ['application-live', id, 'parked-shot'],
    queryFn: () => applicationApi.liveScreenshot(id),
    enabled: parked,
    refetchInterval: 15000,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!shot.data) return
    const objectUrl = URL.createObjectURL(shot.data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [shot.data])
  if (!parked) return null
  const waited = live.data?.waitedSeconds ?? null
  return (
    <section className="panel mb-4 border-info/30 bg-info/[0.06] px-4 py-4 sm:px-5" aria-live="polite">
      <h2 className="text-[14px] font-semibold text-ink">This application is waiting on its live page</h2>
      <p className="mt-1 text-[13px] text-ink-muted">
        The employer&apos;s form is still open on our worker{waited !== null ? ` (for ${describeWait(waited)})` : ''}. Answer what it asks below and press
        <strong> Save &amp; submit</strong>: the run continues on the same screen with your answers — nothing already filled is typed again.
      </p>
      {url ? (
        <div className="mt-3 overflow-hidden rounded-card border border-line">
          <img src={url} alt="The employer's page as it stands on the worker" className="block w-full" />
        </div>
      ) : null}
    </section>
  )
}

function describeWait(seconds: number): string {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} s`
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`
  return `${(seconds / 3600).toFixed(1)} h`
}
